import {
  Configuration,
  CreateRateTable,
  DispatchApi,
  RateTablesApi,
  RatingApi,
  CarrierTariffEffectiveApi,
  CreateCarrierTariffEffective,
  FreightAllKindsMappingApi,
  CreateFreightAllKindsMapping,
  ProvidersApi,
  ProviderCode,
  TransportationMode,
  Account,
  UpdateRateTable
} from '@shipwell/genesis-sdk';
import {getAccessToken} from 'App/api/utils';

const basePath = process.env.SHIPWELL_GENESIS_API_BASE_PATH;

const rateTablesApi = new RateTablesApi(
  new Configuration({
    basePath,
    apiKey: getAccessToken
  })
);

const ratingApi = new RatingApi(
  new Configuration({
    basePath,
    apiKey: getAccessToken
  })
);

const dispatchApi = new DispatchApi(
  new Configuration({
    basePath,
    apiKey: getAccessToken
  })
);

const carrierTariffEffectiveApi = new CarrierTariffEffectiveApi(
  new Configuration({
    basePath,
    apiKey: getAccessToken
  })
);

const freightAllKindsMappingApi = new FreightAllKindsMappingApi(
  new Configuration({
    basePath,
    apiKey: getAccessToken
  })
);

const providersApi = new ProvidersApi(
  new Configuration({
    basePath,
    apiKey: getAccessToken
  })
);

export const createServiceLevel = (createRateTable: CreateRateTable) => {
  return rateTablesApi.createRateTableRatesRateTablesPost(createRateTable);
};

type RateTableRowsParameters = Parameters<
  typeof RateTablesApi.prototype.getRateTableRowsRatesRateTablesRateTableIdRowsGet
>;
type RateTableRowNamedParameters = {
  rateTableId: RateTableRowsParameters[0];
  page?: RateTableRowsParameters[1];
  limit?: RateTableRowsParameters[2];
};

export const getRateTableRows = ({rateTableId, page, limit}: RateTableRowNamedParameters) => {
  return rateTablesApi.getRateTableRowsRatesRateTablesRateTableIdRowsGet(rateTableId, page, limit);
};

type FAKTableDataParameters = Parameters<
  typeof FreightAllKindsMappingApi.prototype.getAllFakMappingsRatesFakMappingsGet
>;
type FAKTableDataNamedParameters = {
  page?: FAKTableDataParameters[0];
  limit?: FAKTableDataParameters[1];
  originalNmfcCode?: FAKTableDataParameters[2];
  mappedNmfcCode?: FAKTableDataParameters[3];
  carrierReferenceQualifier?: FAKTableDataParameters[4];
  carrierReferenceValue?: FAKTableDataParameters[5];
};

export const getFAKTableData = ({
  page,
  limit,
  originalNmfcCode,
  mappedNmfcCode,
  carrierReferenceQualifier,
  carrierReferenceValue
}: FAKTableDataNamedParameters) => {
  return freightAllKindsMappingApi.getAllFakMappingsRatesFakMappingsGet(
    page,
    limit,
    originalNmfcCode,
    mappedNmfcCode,
    carrierReferenceQualifier,
    carrierReferenceValue
  );
};

export const upsertFAKTableData = (fakData: Array<CreateFreightAllKindsMapping>) => {
  return freightAllKindsMappingApi.bulkUpsertRatesFakMappingsBulkPost(fakData);
};

type RateTablesParameters = Parameters<typeof RateTablesApi.prototype.getAllRateTablesRatesRateTablesGet>;
type RateTableNamedParameters = {
  page?: RateTablesParameters[0];
  limit?: RateTablesParameters[1];
  serviceLevel?: RateTablesParameters[2];
  carrierReferenceQualifier?: RateTablesParameters[3];
  carrierReferenceValue?: RateTablesParameters[4];
};

export const getRateTables = ({
  page,
  limit,
  serviceLevel,
  carrierReferenceQualifier,
  carrierReferenceValue
}: RateTableNamedParameters) => {
  return rateTablesApi.getAllRateTablesRatesRateTablesGet(
    page,
    limit,
    serviceLevel,
    carrierReferenceQualifier,
    carrierReferenceValue
  );
};

export const deleteRateTable = (rateTableId: string) => {
  return rateTablesApi.deleteRateTableRatesRateTablesRateTableIdDelete(rateTableId);
};

export const updateRateTable = (rateTableId: string, updatedRateTable: UpdateRateTable) => {
  return rateTablesApi.updateRateTableRatesRateTablesRateTableIdPut(rateTableId, updatedRateTable);
};

export const getRatesByRequestId = (requestId: string) => {
  return ratingApi.getRateRequestDispatchesRateRequestsRateRequestIdGet(requestId, ['dispatches', 'failures']);
};

export const getDispatchByDispatchId = (dispatchId: string) => {
  return dispatchApi.getFtlDispatchDispatchFtlDispatchIdGet(dispatchId);
};

export const connectSmc3 = (values: Account) => {
  return providersApi.createProviderProvidersPost({
    provider_code: ProviderCode.Smc3,
    modes: [TransportationMode.Ltl],
    accounts: [
      {
        provider_code: ProviderCode.Smc3,
        account: {
          username: values.username,
          password: values.password,
          license_key: values.license_key
        }
      }
    ]
  });
};

export const getSmc3Account = () => {
  return providersApi.getProviderProvidersProviderCodeGet(ProviderCode.Smc3);
};

export const disconnectSmc3 = () => {
  return providersApi.deleteProviderProvidersProviderCodeDelete(ProviderCode.Smc3);
};

type CarrierTariffsParameters = Parameters<
  typeof CarrierTariffEffectiveApi.prototype.getAllCarrierTariffEffectiveTariffsGet
>;
type CarrierTariffsNamedParameters = {
  page?: CarrierTariffsParameters[0];
  limit?: CarrierTariffsParameters[1];
  tariffName?: CarrierTariffsParameters[2];
  tariffType?: CarrierTariffsParameters[3];
  carrierReferenceQualifier?: CarrierTariffsParameters[4];
  carrierReferenceValue?: CarrierTariffsParameters[5];
};

export const getAllCarrierTariffs = ({
  page,
  limit,
  tariffName,
  tariffType,
  carrierReferenceQualifier,
  carrierReferenceValue
}: CarrierTariffsNamedParameters) => {
  return carrierTariffEffectiveApi.getAllCarrierTariffEffectiveTariffsGet(
    page,
    limit,
    tariffName,
    tariffType,
    carrierReferenceQualifier,
    carrierReferenceValue
  );
};

export const createCarrierTariff = (tariff: CreateCarrierTariffEffective) => {
  return carrierTariffEffectiveApi.createCarrierTariffEffectiveTariffsPost(tariff);
};

export const updateCarrierTariff = ({tariffId, tariff}: {tariffId: string; tariff: CreateCarrierTariffEffective}) => {
  return carrierTariffEffectiveApi.updateCarrierTariffEffectiveTariffsTariffEffectiveIdPut(tariffId, tariff);
};
